import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';

import { wait } from './tools';
import dnsCache from './DNSCache';

const xhr = require('axios/lib/adapters/xhr');
const http = require('axios/lib/adapters/http');

export type EnvType = 'web' | 'node';

// 通用service
class Service {
  [x: string]: any;

  private ins: AxiosInstance;

  private retryTimes = 3;

  constructor(baseURL: string, envType: EnvType, retry: number, cancelToken: CancelToken | null) {
    if (envType !== 'web' && envType !== 'node') {
      throw new Error("[ SMH HTTP ] 'envType' must be 'web' or 'node'");
    }
    this.ins = this.getIns(baseURL, envType === 'web', retry, cancelToken);
    this.retryTimes = retry;

    dnsCache.init(envType === 'node');
    dnsCache.registerIntercepter(this.ins);

    this.setReqInterceptors();
    this.setResInterceptors();
    // @ts-ignore
    return this.ins;
  }

  getIns(baseURL: string, isBrowser: boolean, retry: number, cancelToken: CancelToken | null): AxiosInstance {
    const axiosConfig: AxiosRequestConfig = {
      baseURL: typeof baseURL === 'string' ? baseURL : '',
      adapter: isBrowser ? xhr : http,
      headers: { 'Content-Type': 'application/json;charset=utf-8' },
      // 是否跨站点访问控制请求
      withCredentials: false,
      timeout: 50000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      transformRequest: [
        data => {
          // FIX: 处理重试时 data 已经被 axios 自行 stringify 过了，此时不需要重复 stringify
          if (typeof data === 'string') return data;
          return JSON.stringify(data);
        },
      ],
      /** FIX: 删除此处 transformResponse
       *  原因：axios 本身就有对 response JSON.parse 过了。
       */
      validateStatus(status) {
        // 200 202 204 206返回给response 否则交给catch处理
        return Math.floor(status / 100) === 2; // 200 202 204 206
      },
    };
    if (cancelToken) {
      axiosConfig.cancelToken = cancelToken;
    }

    if (!isBrowser) {
      const http = (window.parent as any).require('http');
      const https = (window.parent as any).require('https');
      axiosConfig.httpAgent = new http.Agent({ keepAlive: true });
      axiosConfig.httpsAgent = new https.Agent({ keepAlive: true });
    }

    const axiosIns = axios.create(axiosConfig);

    return axiosIns;
  }

  // 通用请求拦截器
  private setReqInterceptors(): void {
    this.ins.interceptors.request.use(
      (config: AxiosRequestConfig) => config,
      error => Promise.resolve(error),
    );
  }

  // 通用响应拦截器
  private setResInterceptors(): void {
    this.ins.interceptors.response.use(
      (response): AxiosResponse => response,
      async error => {
        if (error.message.includes('cancel')) return Promise.reject(error);

        if (!error.config.retryCount) {
          error.config.retryCount = 0;
        }

        // 目前先只针对axios本身错误码重试处理，后续再考虑补充其他node错误码
        const needRetryCode = ['ERR_BAD_RESPONSE', 'ETIMEDOUT', 'ERR_NETWORK'];

        // 服务端错误5XX 重试一次请求
        if (error.response) {
          const status = error.response?.status || 0;
          const { config } = error.response;
          if (Math.floor(status / 100) === 5 && config.retryCount < this.retryTimes) {
            config.retryCount++;
            await wait(1000); // 出错后等待1s再发请求
            logger.smh.error(
              `[SMH response: ${config.customAttributes.apiName}] 服务端错误 ${status}, 第 ${config.retryCount} 次重试请求`,
            );
            return this.ins(config);
          }
        } else if (error.toJSON().name === 'Error' || needRetryCode.includes(error.code)) {
          const { config } = error;
          const { code, message } = error.toJSON();
          if (config.retryCount === 0 && code === 'ENOTFOUND') {
            dnsCache.logDnsCacheInfo();
          }
          if (config.retryCount < this.retryTimes) {
            config.retryCount++;
            await wait(1000); // 出错后等待1s再发请求
            logger.smh.error(
              `[SMH response: ${config.customAttributes.apiName}] 本地请求出错: ${JSON.stringify({
                code,
                message,
              })}, 第 ${config.retryCount} 次重试请求`,
            );
            return this.ins(config);
          }
        }
        return Promise.reject(error);
      },
    );
  }
}

export default Service;
